/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'standby',
  SUCCESS: 'success',
  EXECUTE_FAILED: 'executeFailed', // 固有エラー
  FAILED: 'failed', // 共通エラー
};

export default {
  name: 'requirement094',
  data() {
    return {
      phase: PHASE.START, // 初期表示の画面フェーズ
      isLoading: false, // スピナー表示
      systemId: '', // システムID
      modal_requirement_094: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.EXECUTE_FAILED:
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_094 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_094 = false;
    },

    /** 開始確認画面の「OK」ボタン押下 */
    async standbyProcess() {
      await this.standby();
    },

    /** 固有エラー画面の「OK」ボタン押下 */
    async executeProcess() {
      await this.execute();
    },

    /**
     * 固有エラー画面 の「キャンセル」ボタン押下
     * 共通エラー画面の「OK」ボタン押下
     * 停止画面の「OK」ボタン押下
     */
    async exitProcess() {
      // DefaultSession遷移
      await this.exit();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** NonDefaultSession遷移 */
    async standby() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // NonDefaultSession遷移（バックエンドAPI）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby',
          },
          // エラー発生のコールバックを実装
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              await this.execute();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      }
      finally {
        this.isLoading = false;
      }
    },

    /** ルーチン実行、ルーチン結果確認 */
    async execute() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // ルーチン実行、ルーチン結果確認（バックエンドAPI）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute',
          },
          // エラー発生のコールバックを実装
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              //正常終了 : 停止画面
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
              if (response.data.async_code === error.WSP_094_ROUTINE_ERROR) {
                //異常終了 : 固有エラー
                this.phase = PHASE.EXECUTE_FAILED;
                break;
              } else {
                this.showUnexpectedError();
                break;
              }
            default:
              this.showUnexpectedError();
              break;
          }
        }
      }
      finally {
        this.isLoading = false;
      }
    },

    /** DefaultSession遷移処理 */
    async exit() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // DefaultSession遷移（バックエンドAPI）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit',
          },
          // エラー発生のコールバックを実装
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // Wizardを閉じる
              this.closeWizard();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      }
      finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      if (this.phase === PHASE.FAILED) {
        // Wizardを閉じる
        this.closeWizard();
        return;
      }
      // 異常終了：共通エラー表示
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
  },
};